import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { GetClasses } from '@lifeomic/chroma-react/typeUtils';
import { Download, ExternalLink } from '@lifeomic/chromicons';
import ExternalLinkButton from './ExternalLinkButton';
import {
  useActiveAccountId,
  useActiveProjectId,
  useMixpanel,
} from '../../hooks';
import { useIntl, defineMessages } from 'react-intl';
import FileService from '../../services/FileService';
import useService from '../../hooks/useService';
import { useNotify } from '../../hooks/useNotify';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';

const fileService = new FileService();

const dicomContentType = 'application/dicom';

const isViewableContentType = (contentType: string) =>
  [
    dicomContentType,
    'audio/wave',
    'audio/wav',
    'audio/x-wav',
    'audio/x-pn-wav',
    'audio/mpeg',
    'audio/ogg',
    'video/ogg',
    'application/ogg',
    'video/webm',
    'audio/webm',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'text/plain',
    'application/xml',
    'application/octet-stream',
    'text/html',
    'application/json',
  ].includes(contentType);

export const useStyles = makeStyles((_theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
}));

const messages = defineMessages({
  download: {
    id: 'fileManagementControls.download',
    defaultMessage: 'Download',
  },
  open: {
    id: 'fileManagementControls.open',
    defaultMessage: 'Open',
  },
  openError: {
    id: 'fileManagementControls.openError',
    defaultMessage: 'Error opening file',
  },
  downloadError: {
    id: 'fileManagementControls.downloadError',
    defaultMessage: 'Error downloading file',
  },
});

export type FileManagementControlsClasses = GetClasses<typeof useStyles>;

export interface FileManagementControlsOwnProps {
  enableOpen?: boolean;
  enableRequestDownloadLink?: boolean;
  fileId: string;
  fileName?: string;
  contentType?: string;
  tooltipText?: string;
  className?: string;
}

export type FileManagementControlsProps = FileManagementControlsOwnProps;

/**
  @description View note below - you may not need this!

   _   _ _____ _     _   _   _______
  | | | |  _  | |   ( ) | | | | ___ \
  | |_| | | | | |   |/  | | | | |_/ /
  |  _  | | | | |       | | | |  __/
  | | | \ \_/ / |____   | |_| | |
  \_| |_/\___/\_____/    \___/\_|

  You *may* not want to use this component!  If you are embedding these controls
  *outside* of a table, then this is fine; however, if you are rendering
  these *inside* of a table, you'll want to use the following new components:

  - FileDeleteAction
  - FileDownloadAction
  - FileRenameAction

 */
const FileManagementControls = (props: FileManagementControlsProps) => {
  const { formatMessage } = useIntl();
  const classes = useStyles(props);
  const projectId = useActiveProjectId();
  const accountId = useActiveAccountId();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const mixpanel = useMixpanel();

  const [notify] = useNotify();
  const [fetchOpenLink] = useService(fileService.fetchOpenLink);
  const [fetchDownloadLink] = useService(fileService.fetchDownloadLink);

  const handleOpenPdf = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchOpenLink(props.fileId);
      const downloadUrl = result?.data?.downloadUrl;
      if (!downloadUrl) throw new Error('No open url for file');
      window.open(downloadUrl, '_blank');
      mixpanel.track('file_management_controls_pdf_opened', {
        fileId: props.fileId,
      });
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.openError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [fetchOpenLink, formatMessage, mixpanel, notify, props.fileId]);

  const handleDownloadFile = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchDownloadLink(props.fileId);
      const downloadUrl = result?.data?.downloadUrl;
      if (!downloadUrl) throw new Error('No download url for file');
      window.open(downloadUrl, '_blank');
      mixpanel.track('file_management_controls_download_opened', {
        fileId: props.fileId,
        contentType: props.contentType,
      });
    } catch (e) {
      console.error(e);
      notify(formatMessage(messages.downloadError), 'error');
    } finally {
      setIsLoading(false);
    }
  }, [
    fetchDownloadLink,
    formatMessage,
    mixpanel,
    notify,
    props.contentType,
    props.fileId,
  ]);

  return (
    <div className={clsx(classes.root, props.className)}>
      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
      {isViewableContentType(props.contentType) && props.enableOpen && (
        <ExternalLinkButton
          fileId={props.fileId}
          accountId={accountId}
          projectId={projectId}
          tooltipText={props.tooltipText || formatMessage(messages.open)}
          onClick={() => {
            mixpanel.track('file_management_controls_external_link_opened', {
              fileId: props.fileId,
              contentType: props.contentType,
            });
          }}
        />
      )}
      {props.contentType === 'application/pdf' && props.enableOpen && (
        <IconButton
          aria-label={formatMessage(messages.open)}
          icon={ExternalLink}
          onClick={handleOpenPdf}
          size={0}
          disabled={isLoading}
        />
      )}
      {props.enableRequestDownloadLink && (
        <IconButton
          aria-label={formatMessage(messages.download)}
          icon={Download}
          size={0}
          onClick={handleDownloadFile}
          disabled={isLoading}
        />
      )}
    </div>
  );
};

export default React.memo(FileManagementControls);
